import L from "leaflet";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";

interface FitBoundsProps {
	/** Aggregated [lat, lng] positions across all active layers. */
	positions: [number, number][];
	/**
	 * Identity of the current dataset (e.g. organizationId). Bounds are fitted
	 * once per key — subsequent refetches / layer toggles do NOT move the
	 * viewport, so the user's pan & zoom are preserved.
	 */
	fitKey?: string;
}

/**
 * Fits the map to the supplied positions a single time per `fitKey`.
 * Shared across monitoring layers so 30s polling never yanks the viewport.
 */
export function FitBounds({ positions, fitKey }: FitBoundsProps) {
	const map = useMap();
	const fittedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (positions.length === 0) {
			return;
		}
		const key = fitKey ?? "default";
		if (fittedKeyRef.current === key) {
			return;
		}
		fittedKeyRef.current = key;

		if (positions.length === 1) {
			map.setView(positions[0], 14);
			return;
		}
		map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], maxZoom: 15 });
	}, [map, positions, fitKey]);

	return null;
}
